import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { usePortfolioReminderCreateMutation } from "web_myplann/Api/Reminders/usePortfolioReminderCreateMutation";
import { usePropertyReminderCreateMutation } from "web_myplann/Api/Reminders/usePropertyReminderCreateMutation";
import { ReminderEntityTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import {
	getReminderFormSchema,
	REMINDER_FORM_DEFAULT_VALUES,
	type ReminderFormModel,
} from "web_myplann/Reminders/Forms/reminderFormSchema";
import type {
	ReminderEntity,
	ReminderTemplate,
} from "web_myplann/Reminders/Types/ReminderTypes";

type UseReminderCreateFormProps = {
	entity: ReminderEntity;
	templates: ReminderTemplate[];
	onSuccess: () => void;
};

export const useReminderCreateForm = ({
	entity,
	templates,
	onSuccess,
}: UseReminderCreateFormProps) => {
	const { t } = useResource();
	const { mutate: createPortfolioReminder, isPending: isCreatingPortfolio } =
		usePortfolioReminderCreateMutation();
	const { mutate: createPropertyReminder, isPending: isCreatingProperty } =
		usePropertyReminderCreateMutation();

	const resolver = useMemo(
		() => zodResolver(getReminderFormSchema(t, null)),
		[t],
	);

	const form = useForm<ReminderFormModel>({
		resolver,
		mode: "onChange",
		defaultValues: REMINDER_FORM_DEFAULT_VALUES,
	});

	// A new reminder starts as Custom, but the key is entity specific
	// (portfolio_custom / property_custom / vehicle_custom) and only known once
	// the template list is loaded.
	const customTypeKey = templates.find(
		(template) => template.isCustom,
	)?.typeKey;
	const { setValue, getValues } = form;

	useEffect(() => {
		if (customTypeKey === undefined || getValues("typeKey") !== "") {
			return;
		}

		setValue("typeKey", customTypeKey, { shouldValidate: true });
	}, [customTypeKey, getValues, setValue]);

	const submit = form.handleSubmit((model) => {
		if (entity.type === ReminderEntityTypeEnum.Portfolio) {
			createPortfolioReminder({ portfolioId: entity.id, model }, { onSuccess });
			return;
		}

		createPropertyReminder({ propertyId: entity.id, model }, { onSuccess });
	});

	return {
		form,
		submit,
		isSubmitting: isCreatingPortfolio || isCreatingProperty,
	};
};
